import React, { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import PersonIcon from '@material-ui/icons/Person';
import StorefrontIcon from '@material-ui/icons/Storefront';
import CategoryIcon from '@material-ui/icons/Category';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import { Skeleton } from '@material-ui/lab';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { useSnackbar } from 'notistack';
import JString from '@easytech-international-sdn-bhd/jstring';
import moment from 'moment';
import AppContext from '../../Services/AppContext';
import PersistantData from '../../Services/PersistantData';
import History from '../../Services/History';
import CryptData from '../../Helper/CryptData';
import useCookieHook from '../../Services/useCookieHook';
import API_Service, { axiosDefaultConfig, axiosURIencode } from '../../Services/API_Service';
import { getSetting } from '../../Helper/Helper';
import {
	ck_u_role_id,
	ck_u_username,
	empty,
	req_cust_category,
	req_cust_chain,
	req_dashboard_coverage,
	req_dashboard_efficiency,
	req_dashboard_oos,
	req_module_setting,
	req_salesman,
	reset,
} from '../../Helper/Constants';
import { useCommonStyles } from '../../Styles';
import ListView from '../../Components/ListView';
// import HeroKpiCards from './components/HeroKpiCards';
import CoverageModule from './components/CoverageModule';
import EfficiencyModule from './components/EfficiencyModule';
import OOSModule from './components/OOSModule';
import { monthKey } from './helpers';

const useStyles = makeStyles((theme) => ({
	filterBar: {
		display: 'flex',
		alignItems: 'flex-start',
		flexWrap: 'wrap',
		padding: theme.spacing(1.5, 2),
		marginBottom: theme.spacing(2),
	},
	monthPicker: { width: 150 },
	filterGroup: { display: 'flex', flexDirection: 'column' },
	groupLabel: {
		fontSize: 9,
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: 0.8,
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(0.5),
	},
	groupRow: { display: 'flex', alignItems: 'center' },
	divider: {
		width: 1,
		alignSelf: 'stretch',
		backgroundColor: theme.palette.divider,
		margin: theme.spacing(0, 2),
	},
	gap: { width: theme.spacing(1.5) },
	spacer: { height: theme.spacing(2) },
	skeletonGap: { height: theme.spacing(1.5) },
}));

export default function Dashboard() {
	const classes = useStyles();
	const commonStyle = useCommonStyles();
	const [gState] = useContext(AppContext);
	const singleton = PersistantData.getInstance();
	let { translation: language } = gState;
	if (typeof language === 'undefined') {
		language = singleton.getlang();
	}
	const client = gState.client;
	const { enqueueSnackbar } = useSnackbar();
	const [cookies] = useCookieHook([ck_u_role_id, ck_u_username]);
	const defUsername = cookies(ck_u_username);
	let userAccessRole = cookies(ck_u_role_id);
	if (JString(defUsername).isValid()) {
		userAccessRole = JString(userAccessRole).isValid()
			? new CryptData(userAccessRole, defUsername).decryptAES().getValue()
			: 5;
	}

	const [tabPos, setTabPos] = useState(0);
	const [monthDate, setMonthDate] = useState(new Date());
	const [salesperson, setSalesperson] = useState(empty);
	const [graphFrom, setGraphFrom] = useState(moment().subtract(5, 'month').toDate());
	const [graphTo, setGraphTo] = useState(new Date());
	const [covData, setCovData] = useState(null);
	const [effData, setEffData] = useState(null);
	const [oosData, setOosData] = useState(null);
	const [covLoading, setCovLoading] = useState(false);
	const [effLoading, setEffLoading] = useState(false);
	const [oosLoading, setOosLoading] = useState(false);
	const [firstLoad, setFirstLoad] = useState(true);
	const [spList, setSpList] = useState([]);
	const [spView, setSpView] = useState(false);
	const [chainList, setChainList] = useState([]);
	const [chainView, setChainView] = useState(false);
	const [categoryList, setCategoryList] = useState([]);
	const [categoryView, setCategoryView] = useState(false);
	const [oosChain, setOosChain] = useState(empty);
	const [oosCategory, setOosCategory] = useState(empty);

	const month = monthKey(monthDate);
	const prevMonthKey = moment(month + '-01').subtract(1, 'month').format('YYYY-MM');
	const effectiveSp = salesperson && salesperson !== reset ? salesperson : empty;
	const effectiveChain = oosChain && oosChain !== reset ? oosChain : empty;
	const effectiveCategory = oosCategory && oosCategory !== reset ? oosCategory : empty;

	const loadCoverage = (sp) => {
		setCovLoading(true);
		const params = axiosURIencode({
			month,
			graph_from: monthKey(graphFrom),
			graph_to: monthKey(graphTo),
			salesperson: sp,
			company: gState.company || '',
		});
		API_Service.get(`${req_dashboard_coverage}?${params}`, axiosDefaultConfig(client))
			.then(({ data }) => {
				if (data && JString(data.result).toNumber() === 1) {
					setCovData(data.extraData);
				} else {
					setCovData(null);
					enqueueSnackbar((data && data.message) || language.no_internet, { variant: 'warning' });
				}
			})
			.catch(() => enqueueSnackbar(language.no_internet, { variant: 'error' }))
			.finally(() => {
				setCovLoading(false);
				setFirstLoad(false);
			});
	};

	const loadEfficiency = (sp) => {
		setEffLoading(true);
		const params = axiosURIencode({
			month,
			graph_from: monthKey(graphFrom),
			graph_to: monthKey(graphTo),
			salesperson: sp,
			company: gState.company || '',
		});
		API_Service.get(`${req_dashboard_efficiency}?${params}`, axiosDefaultConfig(client))
			.then(({ data }) => {
				if (data && JString(data.result).toNumber() === 1) {
					setEffData(data.extraData);
				} else {
					setEffData(null);
					enqueueSnackbar((data && data.message) || language.no_internet, { variant: 'warning' });
				}
			})
			.catch(() => enqueueSnackbar(language.no_internet, { variant: 'error' }))
			.finally(() => {
				setEffLoading(false);
				setFirstLoad(false);
			});
	};

	const loadOOS = (chain, category) => {
		setOosLoading(true);
		const params = axiosURIencode({
			month,
			graph_from: monthKey(graphFrom),
			graph_to: monthKey(graphTo),
			outlet_chain: chain,
			outlet_category: category,
			company: gState.company || '',
		});
		API_Service.get(`${req_dashboard_oos}?${params}`, axiosDefaultConfig(client))
			.then(({ data }) => {
				if (data && JString(data.result).toNumber() === 1) {
					setOosData(data.extraData);
				} else {
					setOosData(null);
					enqueueSnackbar((data && data.message) || language.no_internet, { variant: 'warning' });
				}
			})
			.catch(() => enqueueSnackbar(language.no_internet, { variant: 'error' }))
			.finally(() => setOosLoading(false));
	};

	// load salesman list + module access check
	useEffect(() => {
		if (!JString(client).isValid()) return;
		API_Service.get(req_module_setting, axiosDefaultConfig(client)).then(({ data }) => {
			const { result, extraData } = data;
			if (JString(result).toNumber() === 1) {
				const modules = getSetting(extraData, 'access_rights', {})[`${userAccessRole}`] || [];
				if (modules.length > 0 && !modules.includes('Dashboard')) {
					const path = JString(modules[0]).replaceInfinity(' ', '').str.toLowerCase();
					if (path) History.push(`/main/${path}`);
				}
			}
		});
		let parameters = '';
		if (gState.company) {
			parameters = `?${axiosURIencode({ company: gState.company })}`;
		}
		API_Service.get(`${req_salesman}${parameters}`, axiosDefaultConfig(client)).then(({ data }) => {
			if (data && JString(data.result).toNumber() === 1) {
				const list = data.extraData.map((s) => ({ name: '(' + s.login + ') ' + s.name, value: s.login_id }));
				list.push({ name: language.salesman, value: reset });
				setSpList(list);
			}
		});
		API_Service.get(req_cust_chain, axiosDefaultConfig(client)).then(({ data }) => {
			if (data && JString(data.result).toNumber() === 1) {
				const list = data.extraData
					.filter((c) => parseInt(c.cust_chain_status) === 1)
					.map((c) => ({ name: c.cust_chain_name, value: c.cust_chain_id }));
				list.push({ name: language.cust_chain, value: reset });
				setChainList(list);
			}
		});
		API_Service.get(req_cust_category, axiosDefaultConfig(client)).then(({ data }) => {
			if (data && JString(data.result).toNumber() === 1) {
				const list = data.extraData
					.filter((c) => parseInt(c.cust_category_status) === 1)
					.map((c) => ({ name: c.cust_category_name, value: c.cust_category_id }));
				list.push({ name: language.cust_category, value: reset });
				setCategoryList(list);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client, language]);

	// reload both modules when table month, agent, or graph range changes
	useEffect(() => {
		if (!JString(client).isValid()) return;
		loadCoverage(effectiveSp);
		loadEfficiency(effectiveSp);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client, monthDate, salesperson, graphFrom, graphTo]);

	// reload OOS module when table month, graph range, or outlet filters change
	useEffect(() => {
		if (!JString(client).isValid()) return;
		loadOOS(effectiveChain, effectiveCategory);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client, monthDate, graphFrom, graphTo, oosChain, oosCategory]);

	const getSpName = (val) => {
		const found = spList.find((s) => String(s.value) === String(val));
		return found ? found.name : empty;
	};

	const onSelectAgent = (val) => {
		setSpView(false);
		if (val !== undefined && val !== null) setSalesperson(val);
	};

	const getChainName = (val) => {
		const found = chainList.find((c) => String(c.value) === String(val));
		return found ? found.name : empty;
	};

	const onSelectChain = (val) => {
		setChainView(false);
		if (val !== undefined && val !== null) setOosChain(val);
	};

	const getCategoryName = (val) => {
		const found = categoryList.find((c) => String(c.value) === String(val));
		return found ? found.name : empty;
	};

	const onSelectCategory = (val) => {
		setCategoryView(false);
		if (val !== undefined && val !== null) setOosCategory(val);
	};

	const onClearOOSFilters = () => {
		setGraphFrom(moment().subtract(5, 'month').toDate());
		setGraphTo(new Date());
		setMonthDate(new Date());
		setOosChain(empty);
		setOosCategory(empty);
	};

	return (
		<div>
			<Tabs
				indicatorColor={'primary'}
				textColor={'inherit'}
				variant={'fullWidth'}
				value={tabPos}
				centered
				onChange={(e, v) => setTabPos(v)}
				aria-label="dashboard tabs">
				<Tab label={language.overview} />
				<Tab label={language.oos} />
			</Tabs>
			<div className={classes.spacer} />
			<Paper className={classes.filterBar} elevation={1}>
				<MuiPickersUtilsProvider utils={DateFnsUtils}>
					<div className={classes.filterGroup}>
						<Typography className={classes.groupLabel}>{language.graph_period}</Typography>
						<div className={classes.groupRow}>
							<DatePicker
								className={classes.monthPicker}
								autoOk
								variant="inline"
								inputVariant="outlined"
								size="small"
								views={['year', 'month']}
								openTo="month"
								format="MMM yyyy"
								label={language.from}
								value={graphFrom}
								onChange={setGraphFrom}
								maxDate={graphTo}
							/>
							<div className={classes.gap} />
							<DatePicker
								className={classes.monthPicker}
								autoOk
								variant="inline"
								inputVariant="outlined"
								size="small"
								views={['year', 'month']}
								openTo="month"
								format="MMM yyyy"
								label={language.to}
								value={graphTo}
								onChange={setGraphTo}
								minDate={graphFrom}
								maxDate={new Date()}
							/>
						</div>
					</div>
					<div className={classes.divider} />
					<div className={classes.filterGroup}>
						<Typography className={classes.groupLabel}>{language.table_period}</Typography>
						<div className={classes.groupRow}>
							<DatePicker
								className={classes.monthPicker}
								autoOk
								variant="inline"
								inputVariant="outlined"
								size="small"
								views={['year', 'month']}
								openTo="month"
								format="MMM yyyy"
								label={language.choose_date}
								value={monthDate}
								onChange={setMonthDate}
								maxDate={new Date()}
							/>
						</div>
					</div>
					<div className={classes.divider} />
					{tabPos === 0 && (
						<div className={classes.filterGroup}>
							<Typography className={classes.groupLabel}>{language.salesman}</Typography>
							<div className={classes.groupRow}>
								<Button
									color="inherit"
									size="small"
									onClick={() => setSpView(true)}
									startIcon={<PersonIcon />}
									endIcon={<KeyboardArrowDownRoundedIcon />}
								>
									<Typography component="p" className={commonStyle.searchBtnFont}>
										{getSpName(salesperson) || language.salesman}
									</Typography>
								</Button>
							</div>
						</div>
					)}
					{tabPos === 1 && (
						<React.Fragment>
							<div className={classes.filterGroup}>
								<Typography className={classes.groupLabel}>{language.cust_chain}</Typography>
								<div className={classes.groupRow}>
									<Button
										color="inherit"
										size="small"
										onClick={() => setChainView(true)}
										startIcon={<StorefrontIcon />}
										endIcon={<KeyboardArrowDownRoundedIcon />}
									>
										<Typography component="p" className={commonStyle.searchBtnFont}>
											{getChainName(oosChain) || language.cust_chain}
										</Typography>
									</Button>
								</div>
							</div>
							<div className={classes.divider} />
							<div className={classes.filterGroup}>
								<Typography className={classes.groupLabel}>{language.cust_category}</Typography>
								<div className={classes.groupRow}>
									<Button
										color="inherit"
										size="small"
										onClick={() => setCategoryView(true)}
										startIcon={<CategoryIcon />}
										endIcon={<KeyboardArrowDownRoundedIcon />}
									>
										<Typography component="p" className={commonStyle.searchBtnFont}>
											{getCategoryName(oosCategory) || language.cust_category}
										</Typography>
									</Button>
								</div>
							</div>
							<div className={classes.divider} />
							<div className={classes.filterGroup}>
								<Typography className={classes.groupLabel}>{' '}</Typography>
								<div className={classes.groupRow}>
									<Button color="inherit" size="small" onClick={onClearOOSFilters}>
										<Typography component="p" className={commonStyle.searchBtnFont}>
											{language.clear_all}
										</Typography>
									</Button>
								</div>
							</div>
						</React.Fragment>
					)}
				</MuiPickersUtilsProvider>
			</Paper>

			{firstLoad ? (
				<div>
					{/* <Skeleton variant="rect" height={92} animation="wave" />
					<div className={classes.skeletonGap} /> */}
					<Skeleton variant="rect" height={380} animation="wave" />
				</div>
			) : (
				<div>
					{/* {covData && effData && (
						<HeroKpiCards
							coverage={covData}
							efficiency={effData}
							prevMonthKey={prevMonthKey}
							language={language}
						/>
					)}
					<div className={classes.spacer} /> */}
					{tabPos === 0 && (
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<CoverageModule
									data={covData}
									loading={covLoading}
									language={language}
									prevMonthKey={prevMonthKey}
									month={month}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<EfficiencyModule
									data={effData}
									loading={effLoading}
									language={language}
									prevMonthKey={prevMonthKey}
									month={month}
								/>
							</Grid>
						</Grid>
					)}
					{tabPos === 1 && (
						<OOSModule
							data={oosData}
							loading={oosLoading}
							language={language}
							prevMonthKey={prevMonthKey}
						/>
					)}
				</div>
			)}

			<ListView
				classes={{ paper: commonStyle.listView }}
				id="agent-menu"
				keepMounted
				open={spView}
				onClose={() => setSpView(false)}
				onSelect={onSelectAgent}
				value={salesperson}
				title={language.salesman || ''}
				language={language}
				options={spList}
			/>
			<ListView
				classes={{ paper: commonStyle.listView }}
				id="chain-menu"
				keepMounted
				open={chainView}
				onClose={() => setChainView(false)}
				onSelect={onSelectChain}
				value={oosChain}
				title={language.cust_chain || ''}
				language={language}
				options={chainList}
			/>
			<ListView
				classes={{ paper: commonStyle.listView }}
				id="category-menu"
				keepMounted
				open={categoryView}
				onClose={() => setCategoryView(false)}
				onSelect={onSelectCategory}
				value={oosCategory}
				title={language.cust_category || ''}
				language={language}
				options={categoryList}
			/>
		</div>
	);
}
